import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Auth from "../../models/Auth";
import { AUTH_SERVICE_URL } from "../../helper/constant";
import qs from "qs";

export const authServiceSlice = createApi({
    reducerPath: "authService",
    baseQuery: fetchBaseQuery({
        baseUrl: `${AUTH_SERVICE_URL}`,
        prepareHeaders: (headers) => {
            headers.set("Authorization", "Bearer " + Auth.token);
            headers.set("x-portal", Auth.portalId);
            headers.set("x-userId", Auth.userId);
            return headers;
        },
    }),
    tagTypes: ["PortalUserGroup"],
    endpoints: (builder) => ({
        getPortalCompanies: builder.query({
            query: (payload) => {
                const queryStr = qs.stringify({
                    portalId: payload.portalId,
                });
                return {
                    url: `/portalcompanies/listPortalCompanies?${queryStr}`,
                    method: "GET",
                };
            },
            transformResponse: (responseData) => responseData.data,
            providesTags: (result, error, arg) => ["PortalCompanies"],
        }),
        getPortalUserCompanies: builder.query({
            query: (payload) => {
                return {
                    url: `/portalusercompanies/getList`,
                    method: "POST",
                    body: payload,
                };
            },
            transformResponse: (responseData) => responseData,
            providesTags: (result, error, arg) => ["PortalUserCompanies"],
        }),
        createPortalCompany: builder.mutation({
            query: ({ name, countryId, state, streetAdd1, streetAdd2, city, postCode, contacts, servingCountries }) => ({
                url: `/portalcompanies/createNew`,
                method: "POST",
                body: {
                    name,
                    countryId,
                    state,
                    streetAdd1,
                    streetAdd2,
                    city,
                    postCode,
                    contacts,
                    servingCountries,
                    portalId: Auth.portalId,
                    userId: Auth.userId,
                },
            }),
            async onQueryStarted({ name, countryId, state, streetAdd1, streetAdd2, city, postCode, contacts, servingCountries }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    authServiceSlice.util.updateQueryData("getPortalCompanies", undefined, (companies) => {
                        companies.push({
                            name,
                            countryId,
                            state,
                            streetAdd1,
                            streetAdd2,
                            city,
                            postCode,
                            contacts,
                            servingCountries,
                            portalId: Auth.portalId,
                            id: `${Auth.portalId}-${name.trim()}-${countryId}`,
                        });
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        updatePortalCompany: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: `/portalcompanies/updatePortalCompany/${id}`,
                method: "POST",
                body: { ...rest, portalId: Auth.portalId },
            }),
            async onQueryStarted({ id, ...rest }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    authServiceSlice.util.updateQueryData("getPortalCompanies", undefined, (companies) => {
                        const existCompany = companies.find((company) => company.id === id);
                        const existIndex = companies.findIndex((company) => company.id === id);
                        const updatedCompany = { ...existCompany, ...rest, portalId: Auth.portalId };
                        companies.splice(existIndex, 1, updatedCompany);
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        deletePortalCompany: builder.mutation({
            query: ({ id }) => ({
                url: `/portalcompanies/deletePortalCompany/${id}`,
                method: "POST",
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    authServiceSlice.util.updateQueryData("getPortalCompanies", undefined, (companies) => {
                        companies.splice(
                            companies.findIndex((company) => company.id === id),
                            1
                        );
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        getCompanyContacts: builder.query({
            query: () => ({
                url: `/companycontacts/getCompanyContacts`,
                method: "GET",
                params: {
                    portalId: Auth.portalId,
                },
            }),
            transformResponse: (responseData) => responseData.data,
            providesTags: (result, error, arg) => [{ type: "CompanyContacts" }],
        }),
        createCompanyContact: builder.mutation({
            query: ({ name, phone, email, companies }) => ({
                url: `/companycontacts/createCompanyContact`,
                method: "POST",
                body: { name, phone, email, companies, portalId: Auth.portalId },
            }),
            invalidatesTags: (result, error, arg) => [{ type: "CompanyContacts" }],
            async onQueryStarted({ name, phone, email, companies }, { dispatch, queryFulfilled }) {
                try {
                    const {
                        data: { data: createdContact },
                    } = await queryFulfilled;
                    dispatch(
                        authServiceSlice.util.updateQueryData("getPortalCompanies", undefined, (draft) => {
                            const existCompany = draft.find((company) => company.id === createdContact.companies[0].id);
                            existCompany.contacts.push({ name, phone, email, companies, portalId: Auth.portalId, id: createdContact.id });
                        })
                    );
                } catch (error) {
                    console.log(error);
                }
            },
        }),
        updateContactOfCompany: builder.mutation({
            query: ({ companyId, contactId, ...rest }) => ({
                url: `/companycontacts/updateCompanyContact/${contactId}`,
                method: "POST",
                body: { ...rest, portalId: Auth.portalId },
            }),
            invalidatesTags: (result, error, arg) => [{ type: "CompanyContacts" }],
            async onQueryStarted({ companyId, contactId, ...rest }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    authServiceSlice.util.updateQueryData("getPortalCompanies", undefined, (companies) => {
                        const existCompany = companies.find((company) => company.id === companyId);
                        const existContact = existCompany.contacts.find((contact) => contact.id === contactId);
                        Object.keys(rest).forEach((key) => (existContact[key] = rest[key]));
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        deleteCompanyContact: builder.mutation({
            query: ({ id }) => ({
                url: `/companycontacts/deleteCompanyContact`,
                method: "POST",
                body: { id: id },
            }),
            invalidatesTags: (result, error, arg) => [{ type: "CompanyContacts" }],
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    authServiceSlice.util.updateQueryData("getPortalCompanies", undefined, (companies) => {
                        const existCompany = companies.find((company) => company.contacts.findIndex((contact) => contact.id === id) > -1);
                        existCompany.contacts.splice(
                            existCompany.contacts.findIndex((contact) => contact.id === id),
                            1
                        );
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
    }),
});

export const {
    useGetPortalCompaniesQuery,
    useGetPortalUserCompaniesQuery,
    useLazyGetPortalCompaniesQuery,
    useCreatePortalCompanyMutation,
    useUpdatePortalCompanyMutation,
    useDeletePortalCompanyMutation,
    useGetCompanyContactsQuery,
    useCreateCompanyContactMutation,
    useUpdateContactOfCompanyMutation,
    useDeleteCompanyContactMutation,
} = authServiceSlice;
